import { useQueryClient } from "@tanstack/react-query";

import { type InvalidateQueryOptions, QueryModule, invalidateQueries } from "@/data/invalidateQueries";
import { useAuthGuardedMutation } from "@/hooks/useAuthGuardedMutation";
import { useAuthGuardedQuery } from "@/hooks/useAuthGuardedQuery";
import type { AppMutationOptions, AppQueryOptions } from "@/types/react-query";

import { BizTagsApi } from "./bizTags.api";
import type { BizTagsModels } from "./bizTags.models";

export namespace BizTagsQueries {
  export const moduleName = QueryModule.BizTags;

  export const keys = {
    all: [moduleName] as const,
    listTags: (limit: number, order?: string, page?: number, cursor?: string, filterName?: string) =>
      [...keys.all, "/biz/tag/list", limit, order, page, cursor, filterName] as const,
  };

  /**
   * Query `useListTags`
   * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
   * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): name, count. Example: `name`
   * @param { number } object.page Query parameter. 1-indexed page number to begin from
   * @param { string } object.cursor Query parameter. ID of item to start after
   * @param { string } object.filterName Query parameter. Filter by tag name (partial match). Example: `Professional`
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizTagsModels.ListTagsResponse> }
   * @statusCodes [200]
   */
  export const useListTags = <TData>(
    {
      limit,
      order,
      page,
      cursor,
      filterName,
    }: { limit: number; order?: string; page?: number; cursor?: string; filterName?: string },
    options?: AppQueryOptions<typeof BizTagsApi.listTags, TData>,
  ) => {
    return useAuthGuardedQuery({
      queryKey: keys.listTags(limit, order, page, cursor, filterName),
      queryFn: () => BizTagsApi.listTags(limit, order, page, cursor, filterName),
      ...options,
    });
  };

  /**
   * Mutation `useCreateTag`
   * @param { BizTagsModels.CreateBizTagDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<CommonModels.BizTagDto> }
   * @statusCodes [201, default]
   */
  export const useCreateTag = (
    options?: AppMutationOptions<typeof BizTagsApi.createTag, { data: BizTagsModels.CreateBizTagDto }> &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizTagsApi.createTag(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Mutation `useDeleteTag`
   * @param { string } mutation.id Path parameter. Tag ID
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<void> }
   * @statusCodes [204]
   */
  export const useDeleteTag = (
    options?: AppMutationOptions<typeof BizTagsApi.deleteTag, { id: string }> & InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ id }) => BizTagsApi.deleteTag(id),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Mutation `useUpdateTag`
   * @param { string } mutation.id Path parameter. Tag ID
   * @param { BizTagsModels.UpdateBizTagDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<CommonModels.BizTagDto> }
   * @statusCodes [200, default]
   */
  export const useUpdateTag = (
    options?: AppMutationOptions<typeof BizTagsApi.updateTag, { id: string; data: BizTagsModels.UpdateBizTagDto }> &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ id, data }) => BizTagsApi.updateTag(id, data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };
}
